import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Platform,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Brand, Neutrals, primaryAlpha } from '@/constants/theme';
import { useAuth } from '@/hooks/use-auth';
import { useJoinHousehold } from '@/hooks/useHousehold';
import { parseInviteTokenFromInput, PENDING_INVITE_KEY } from '@/lib/invite-links';
import { supabase } from '@/lib/supabase';

/** Validate token via RPC (returns household_id if valid) */
async function validateInviteToken(token: string): Promise<boolean> {
  const { data } = await supabase.rpc('get_household_id_for_invite_token', {
    token,
  });
  return !!data;
}

export default function JoinInviteScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const params = useLocalSearchParams<{ token?: string }>();
  const rawParam = typeof params.token === 'string' ? params.token : undefined;
  const urlTokenFromParams = rawParam?.trim() ? parseInviteTokenFromInput(rawParam.trim()) || undefined : undefined;
  const [urlToken, setUrlToken] = useState<string | undefined>(urlTokenFromParams);
  const { joinHousehold, loading: joining, error } = useJoinHousehold();

  const [manualInput, setManualInput] = useState('');
  const [validating, setValidating] = useState(!!urlTokenFromParams);
  const [validToken, setValidToken] = useState<string | null>(urlTokenFromParams || null);
  const [validateError, setValidateError] = useState<string | null>(null);

  // If not signed in, redirect to sign-in and pass token in URL so sign-in can redirect back after auth
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      const raw = urlTokenFromParams || urlToken;
      const tokenToPass = raw?.trim() ? parseInviteTokenFromInput(raw.trim()) || undefined : undefined;
      if (tokenToPass) {
        console.log('[JoinInvite] Redirecting to sign-in with inviteToken in URL', { tokenPreview: `${tokenToPass.slice(0, 8)}...` });
        router.replace(`/(auth)/sign-in?inviteToken=${encodeURIComponent(tokenToPass)}` as any);
      } else {
        AsyncStorage.getItem(PENDING_INVITE_KEY).then((stored) => {
          const raw = stored?.trim();
          const t = raw ? parseInviteTokenFromInput(raw) || undefined : undefined;
          if (t) {
            console.log('[JoinInvite] Redirecting to sign-in with inviteToken from storage', { tokenPreview: `${t.slice(0, 8)}...` });
            router.replace(`/(auth)/sign-in?inviteToken=${encodeURIComponent(t)}` as any);
          } else {
            console.log('[JoinInvite] Redirecting to sign-in without token (none in URL or storage)');
            router.replace('/(auth)/sign-in');
          }
        });
      }
      return;
    }
  }, [isAuthenticated, authLoading, router, urlTokenFromParams, urlToken]);

  // Pre-fill token from AsyncStorage if URL had no token (e.g. redirect dropped query)
  useEffect(() => {
    if (urlTokenFromParams) return;
    let cancelled = false;
    AsyncStorage.getItem(PENDING_INVITE_KEY).then((stored) => {
      if (!cancelled && stored?.trim()) {
        setUrlToken(stored.trim());
        setValidating(true);
        setValidateError(null);
        validateInviteToken(stored.trim())
          .then((valid) => {
            if (!cancelled) {
              setValidToken(valid ? stored.trim() : null);
              setValidateError(valid ? null : 'This invite link is invalid or has expired.');
            }
          })
          .catch(() => {
            if (!cancelled) {
              setValidToken(null);
              setValidateError('Could not validate invite. Please try again.');
            }
          })
          .finally(() => {
            if (!cancelled) setValidating(false);
          });
      }
    });
    return () => { cancelled = true; };
  }, [urlTokenFromParams]);

  // When we have a token from URL params, validate it on mount
  useEffect(() => {
    if (!urlTokenFromParams || urlToken !== urlTokenFromParams) return;
    let cancelled = false;
    setValidating(true);
    setValidateError(null);
    validateInviteToken(urlTokenFromParams)
      .then((valid) => {
        if (!cancelled) {
          setValidToken(valid ? urlTokenFromParams : null);
          setValidateError(valid ? null : 'This invite link is invalid or has expired.');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setValidToken(null);
          setValidateError('Could not validate invite. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when URL param changes; urlToken is set by this effect
  }, [urlTokenFromParams]);

  const handleContinue = async () => {
    const token = parseInviteTokenFromInput(manualInput);
    if (!token) {
      setValidateError('Please paste an invite link or token.');
      return;
    }
    setValidateError(null);
    setValidating(true);
    try {
      const valid = await validateInviteToken(token);
      if (valid) {
        setValidToken(token);
      } else {
        setValidToken(null);
        setValidateError('This invite link is invalid or has expired.');
      }
    } catch {
      setValidToken(null);
      setValidateError('Could not validate invite. Please try again.');
    } finally {
      setValidating(false);
    }
  };

  const handleAccept = async () => {
    if (!validToken) return;
    try {
      const household = await joinHousehold(validToken);
      if (household) router.replace('/(tabs)/settings');
    } catch {
      // Error set by hook
    }
  };

  const handleDecline = () => {
    setValidToken(null);
    setManualInput('');
    setValidateError(null);
    AsyncStorage.removeItem(PENDING_INVITE_KEY);
    router.replace('/(tabs)/settings');
  };

  const handleBack = () => {
    router.replace('/(tabs)/settings');
  };

  const showAcceptDecline = validToken && !validating;
  const showManualEntry = !urlToken || (!validating && !validToken);

  if (authLoading || !isAuthenticated) {
    return null;
  }

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          { paddingTop: Math.max(insets.top, 16) },
        ]}
        keyboardShouldPersistTaps="handled"
      >
        <TouchableOpacity
          style={styles.backButton}
          onPress={handleBack}
          accessibilityLabel="Go back"
        >
          <Ionicons name="arrow-back" size={20} color={Brand.primary} />
          <Text style={styles.backText}>Back</Text>
        </TouchableOpacity>

        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Ionicons name="person-add" size={48} color={Brand.primary} />
          </View>
          <Text style={styles.title}>Join a household</Text>
          <Text style={styles.subtitle}>
            Enter an invite link or paste the code to join someone else&apos;s household.
          </Text>
        </View>

        {(validating || (urlToken && !showAcceptDecline && !showManualEntry)) && (
          <View style={[styles.card, styles.cardCentered]}>
            <ActivityIndicator size="large" color={Brand.primary} />
            <Text style={styles.loadingText}>
              {validating ? 'Checking invite…' : 'Loading…'}
            </Text>
          </View>
        )}

        {showManualEntry && (
          <View style={styles.card}>
            <Text style={styles.label}>Invite link or code</Text>
            <Input
              value={manualInput}
              onChangeText={(t) => {
                setManualInput(t);
                setValidateError(null);
              }}
              placeholder="Paste link or token"
              style={styles.input}
              editable={!validating}
            />
            {validateError && (
              <Text style={styles.errorText} testID="invite-error-message">{validateError}</Text>
            )}
            <Button
              onPress={handleContinue}
              disabled={validating}
              style={styles.submitButton}
            >
              {validating ? (
                <ActivityIndicator size="small" color={Neutrals.white} />
              ) : (
                'Continue'
              )}
            </Button>
          </View>
        )}

      </ScrollView>

      <Modal
        visible={showAcceptDecline}
        transparent
        animationType="fade"
        onRequestClose={handleDecline}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Join this household?</Text>
            <Text style={styles.modalDescription}>
              You&apos;ll be able to see and edit shared grocery lists and more.
            </Text>
            {error && (
              <Text style={styles.modalErrorText} testID="invite-error-message">{error.message}</Text>
            )}
            <View style={styles.modalActions}>
              <Button
                testID="decline-invite-btn"
                variant="outline"
                onPress={handleDecline}
                disabled={joining}
                style={styles.modalButton}
              >
                Decline
              </Button>
              <Button
                testID="accept-invite-btn"
                onPress={handleAccept}
                disabled={joining}
                style={styles.modalButton}
              >
                {joining ? (
                  <ActivityIndicator size="small" color={Neutrals.white} />
                ) : (
                  'Accept'
                )}
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginBottom: 24,
    paddingVertical: 4,
  },
  backText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
  },
  header: {
    marginBottom: 32,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: primaryAlpha(0.2),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    lineHeight: 24,
  },
  card: {
    backgroundColor: Neutrals.whiteFaded90,
    borderRadius: 20,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.06)',
    marginBottom: 16,
  },
  cardCentered: {
    alignItems: 'center',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginBottom: 8,
    alignSelf: 'stretch',
  },
  input: {
    marginBottom: 16,
  },
  errorText: {
    fontSize: 14,
    color: Neutrals.destructiveMuted,
    marginBottom: 16,
    alignSelf: 'stretch',
  },
  submitButton: {
    marginTop: 8,
    alignSelf: 'stretch',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: Neutrals.textSecondary,
  },
  inviteTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  inviteDescription: {
    fontSize: 14,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  actionButton: {
    flex: 1,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: Neutrals.whiteFaded90,
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 10,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 14,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
  },
  modalErrorText: {
    fontSize: 14,
    color: Brand.destructive,
    marginBottom: 16,
    textAlign: 'center',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  modalButton: {
    flex: 1,
  },
});
